
import React, { useRef, useState, useEffect } from 'react';
import { VenueConfig, TableConfig, DanceFloorConfig, CustomElementConfig, Theme } from '../types';
import TableItem from './TableItem';
import DanceFloorItem from './DanceFloorItem';
import CustomElementItem from './CustomElementItem'; 
import Ruler from './Ruler'; 

interface FloorPlanCanvasProps {
  venueConfig: VenueConfig;
  tables: TableConfig[];
  danceFloor: DanceFloorConfig;
  customElements: CustomElementConfig[];
  onUpdateTable: (updatedTable: TableConfig) => void;
  onUpdateDanceFloor: (updatedDanceFloor: DanceFloorConfig) => void;
  onUpdateCustomElement: (updatedCustomElement: CustomElementConfig) => void;
  onSelectTable: (id: string | null) => void;
  selectedTableId: string | null;
  onRotateTable: (id: string) => void;
  onOpenGuestModal: (id: string) => void;
  handleRotateDanceFloor: () => void;
  onRotateCustomElement: (id: string) => void; 
  onSelectDanceFloor: () => void;
  onSelectCustomElement: (id: string | null) => void; 
  isDanceFloorSelected: boolean;
  selectedCustomElementId: string | null; 
  theme: Theme;
  canvasRef: React.RefObject<HTMLDivElement>; 
}

const RULER_SIZE = 25; 
const GRID_SPACING_METERS = 1; 

const FloorPlanCanvas: React.FC<FloorPlanCanvasProps> = ({
  venueConfig,
  tables,
  danceFloor,
  customElements, 
  onUpdateTable,
  onUpdateDanceFloor,
  onUpdateCustomElement, 
  onSelectTable,
  selectedTableId,
  onRotateTable,
  onOpenGuestModal,
  handleRotateDanceFloor,
  onRotateCustomElement, 
  onSelectDanceFloor,
  onSelectCustomElement, 
  isDanceFloorSelected,
  selectedCustomElementId, 
  theme,
  canvasRef 
}) => {
  const internalContainerRef = useRef<HTMLDivElement>(null); 
  const [pixelScale, setPixelScale] = useState(20);
  const [viewScale, setViewScale] = useState(1); // Added for Zoom
  const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const calculateScaleAndDimensions = () => {
      if (internalContainerRef.current && venueConfig.width > 0 && venueConfig.length > 0) {
        const availableWidth = internalContainerRef.current.offsetWidth - RULER_SIZE - 40; // Margin for aesthetics
        const availableHeight = internalContainerRef.current.offsetHeight - RULER_SIZE - 40;
        
        const scaleX = availableWidth / venueConfig.width;
        const scaleY = availableHeight / venueConfig.length;
        
        // Fit logic: use the smaller scale to fit entirely, but clamp max scale for small venues
        const newScale = Math.min(scaleX, scaleY, 40); 
        setPixelScale(newScale);
        setCanvasDimensions({
          width: venueConfig.width * newScale,
          height: venueConfig.length * newScale,
        });
      } else {
        setCanvasDimensions({ width: 0, height: 0 });
      }
    };

    calculateScaleAndDimensions();
    const resizeObserver = new ResizeObserver(calculateScaleAndDimensions);
    if (internalContainerRef.current) {
        resizeObserver.observe(internalContainerRef.current);
    }
    return () => resizeObserver.disconnect();
  }, [venueConfig.width, venueConfig.length]);

  // Zoom Handlers
  const handleZoomIn = () => setViewScale(prev => Math.min(prev + 0.1, 2.0));
  const handleZoomOut = () => setViewScale(prev => Math.max(prev - 0.1, 0.5));
  const handleResetZoom = () => setViewScale(1);

  const handleTableDrag = (id: string, x: number, y: number) => {
    const table = tables.find(t => t.id === id);
    if (table) {
        onUpdateTable({ ...table, x, y });
    }
  };
  
  const handleDanceFloorDrag = (x: number, y: number) => {
    onUpdateDanceFloor({ ...danceFloor, x, y });
  };

  const handleCustomElementDrag = (id: string, x: number, y: number) => {
    const element = customElements.find(el => el.id === id);
    if (element) {
        onUpdateCustomElement({ ...element, x, y });
    }
  };

  // --- ESTILOS DE PAPEL (CANVAS) ---
  const canvasBgColor = theme === 'dark' ? 'bg-[#1e1e2e]' : 'bg-white'; 
  const canvasBorderColor = theme === 'dark' ? 'border-slate-600' : 'border-slate-300';
  const gridLineColor = theme === 'dark' ? 'rgba(176, 176, 176, 0.05)' : 'rgba(107, 114, 128, 0.1)';
  const gridLineColorMajor = theme === 'dark' ? 'rgba(176, 176, 176, 0.15)' : 'rgba(107, 114, 128, 0.2)';

  // Background Dot Pattern for the PAPER
  const paperPattern = theme === 'dark' 
    ? 'radial-gradient(circle, rgba(255,255,255,0.07) 1px, transparent 1px)' 
    : 'radial-gradient(circle, rgba(0,0,0,0.1) 1px, transparent 1px)';
  const paperPatternSize = '20px 20px';

  // --- ESTILOS DE ESCRITORIO (WORKSPACE/CONTAINER) ---
  // Fondo más oscuro y técnico para resaltar el papel blanco/oscuro
  const workspaceBgClass = theme === 'dark' ? 'bg-[#050508]' : 'bg-zinc-200';
  const workspaceBorderClass = theme === 'dark' ? 'border-hba-mid-purple/30' : 'border-zinc-300';
  
  // Patrón más espaciado para el escritorio
  const workspacePattern = theme === 'dark'
    ? 'radial-gradient(circle, rgba(255,255,255,0.03) 1px, transparent 1px)'
    : 'radial-gradient(circle, rgba(0,0,0,0.05) 1px, transparent 1px)';

  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
     if (e.target === canvasRef.current || (canvasRef.current && canvasRef.current.contains(e.target as Node) && (e.target as HTMLElement).dataset?.canvasId === "floor-plan-main") ) {
        if (e.target === canvasRef.current || (e.target as HTMLElement).dataset?.canvasId === "floor-plan-main-bg") {
             onSelectTable(null);
             if (isDanceFloorSelected) onSelectDanceFloor(); 
             if (selectedCustomElementId) onSelectCustomElement(null);
        }
        return;
    }
    if (e.target === internalContainerRef.current || (canvasRef.current && e.target === canvasRef.current.querySelector('[data-canvas-id="floor-plan-main-bg"]')) ) {
      onSelectTable(null);
      if (isDanceFloorSelected) onSelectDanceFloor(); 
      if (selectedCustomElementId) onSelectCustomElement(null);
    }
  };
  
  const renderGrid = () => {
    if (!canvasDimensions.width || !canvasDimensions.height || pixelScale <= 0) return null;
    const gridLines = [];
    const gridSpacingPx = GRID_SPACING_METERS * pixelScale;

    for (let x = gridSpacingPx; x < canvasDimensions.width; x += gridSpacingPx) {
      const isMajor = Math.round(x / gridSpacingPx) % 5 === 0;
      gridLines.push(
        <div
          key={`grid-v-${x}`}
          className="absolute top-0 bottom-0 pointer-events-none"
          style={{ left: `${x}px`, width: '1px', backgroundColor: isMajor ? gridLineColorMajor : gridLineColor, zIndex: 0 }}
        />
      );
    }
    for (let y = gridSpacingPx; y < canvasDimensions.height; y += gridSpacingPx) {
       const isMajor = Math.round(y / gridSpacingPx) % 5 === 0;
      gridLines.push(
        <div
          key={`grid-h-${y}`}
          className="absolute left-0 right-0 pointer-events-none"
          style={{ top: `${y}px`, height: '1px', backgroundColor: isMajor ? gridLineColorMajor : gridLineColor, zIndex: 0 }}
        />
      );
    }
    return gridLines;
  };

  return (
    <div className="relative flex flex-col w-full max-w-4xl mx-auto h-full">
        {/* Zoom Controls Overlay */}
        <div className="absolute top-4 right-4 z-40 flex flex-col gap-2 shadow-xl">
            <div className="flex flex-col bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <button onClick={handleZoomIn} className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 text-xl font-bold text-hba-accent-purple dark:text-white transition-colors focus:outline-none" title="Acercar">+</button>
                <div className="h-px w-full bg-gray-200 dark:bg-gray-700"></div>
                <span className="w-9 h-6 flex items-center justify-center text-[10px] font-mono font-bold text-gray-700 dark:text-white bg-gray-50 dark:bg-gray-800/50 select-none">{Math.round(viewScale * 100)}%</span>
                <div className="h-px w-full bg-gray-200 dark:bg-gray-700"></div>
                <button onClick={handleZoomOut} className="w-9 h-9 flex items-center justify-center hover:bg-gray-100 dark:hover:bg-gray-700 text-xl font-bold text-hba-accent-purple dark:text-white transition-colors focus:outline-none" title="Alejar">-</button>
            </div>
            <button onClick={handleResetZoom} className="w-9 h-9 flex items-center justify-center bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 text-[10px] font-bold text-gray-600 dark:text-gray-200 transition-colors shadow-sm" title="Restablecer">FIT</button>
        </div>

        <div 
        ref={internalContainerRef} 
        className={`w-full h-[75vh] overflow-hidden flex justify-center items-center rounded-xl border shadow-inner relative transition-colors duration-300 ${workspaceBgClass} ${workspaceBorderClass}`}
        style={{
            backgroundImage: workspacePattern,
            backgroundSize: '40px 40px'
        }}
        onClick={handleCanvasClick} 
        >
        {canvasDimensions.width > 0 && canvasDimensions.height > 0 ? (
            <div 
                style={{ 
                    transform: `scale(${viewScale})`, 
                    transformOrigin: 'center center',
                    transition: 'transform 0.2s ease-out'
                }}
                className="shadow-[0_20px_50px_rgba(0,0,0,0.3)] dark:shadow-[0_20px_50px_rgba(0,0,0,0.5)]" // Sombra profunda para levantar el plano del escritorio
            >
            <div ref={canvasRef} className="relative" style={{ width: canvasDimensions.width + RULER_SIZE, height: canvasDimensions.height + RULER_SIZE }}>
            <Ruler orientation="horizontal" length={venueConfig.width} pixelScale={pixelScale} size={RULER_SIZE} theme={theme} />
            <Ruler orientation="vertical" length={venueConfig.length} pixelScale={pixelScale} size={RULER_SIZE} theme={theme} />
            <div 
                data-canvas-id="floor-plan-main-bg" 
                className={`absolute border ${canvasBorderColor} ${canvasBgColor} overflow-hidden`}
                style={{
                    width: `${canvasDimensions.width}px`,
                    height: `${canvasDimensions.height}px`,
                    top: `${RULER_SIZE}px`,
                    left: `${RULER_SIZE}px`,
                    position: 'relative', 
                    backgroundImage: paperPattern,
                    backgroundSize: paperPatternSize
                }}
                
            >
                {renderGrid()}
                
                {/* Items Layer */}
                {tables.map((table) => (
                <TableItem
                    key={table.id}
                    table={table}
                    pixelScale={pixelScale}
                    onDrag={handleTableDrag}
                    onRotate={onRotateTable}
                    onOpenGuestModal={onOpenGuestModal}
                    isSelected={selectedTableId === table.id}
                    onSelect={onSelectTable}
                    venueWidthPx={canvasDimensions.width}
                    venueHeightPx={canvasDimensions.height}
                    theme={theme}
                />
                ))}
                {danceFloor.enabled && danceFloor.width > 0 && danceFloor.height > 0 && (
                <DanceFloorItem
                    danceFloor={danceFloor}
                    pixelScale={pixelScale}
                    onDrag={handleDanceFloorDrag}
                    onRotate={handleRotateDanceFloor}
                    onSelect={onSelectDanceFloor}
                    isSelected={isDanceFloorSelected}
                    venueWidthPx={canvasDimensions.width}
                    venueHeightPx={canvasDimensions.height}
                    theme={theme}
                />
                )}
                {customElements.map((element) => (
                <CustomElementItem
                    key={element.id}
                    element={element}
                    pixelScale={pixelScale}
                    onDrag={handleCustomElementDrag}
                    onRotate={onRotateCustomElement}
                    isSelected={selectedCustomElementId === element.id}
                    onSelect={onSelectCustomElement}
                    venueWidthPx={canvasDimensions.width}
                    venueHeightPx={canvasDimensions.height}
                    theme={theme}
                />
                ))}
            </div>
            </div>
            </div>
        ) : (
            <div className={`flex items-center justify-center h-full w-full z-10`}>
            <p className={`${theme === 'dark' ? 'text-hba-text-secondary' : 'text-gray-500'} font-medium`}>
                {venueConfig.width <= 0 || venueConfig.length <=0 ? "Configure las dimensiones para ver el plano." : "Ajustando mesa de trabajo..."}
            </p>
            </div>
        )}
        </div>
    </div>
  );
};

export default FloorPlanCanvas;
